// 浏览器语言非中文时，自动注入 Google 翻译，把整站翻译成对应语言。
// 测试参数：URL 加 ?lang=en 强制翻译成英文；?lang=zh|off 关闭；不带参数则按浏览器语言判断。
//
// 目标语言解析优先级（resolveTargetLang）：
//   1) URL ?lang= 显式指定（最高优先级，方便测试）
//   2) 已存在的 googtrans cookie（用户上次手动选择的语言，尊重之）
//   3) 浏览器首选语言非中文 → 映射成 Google 语言码
//   4) 否则返回 ''（保持中文，不翻译）
//
// 调用：app.mount 之后执行 initTranslate()，让 Vue 首屏 DOM 先渲染出来再翻译。
// 注意：Google 翻译会改写 DOM 文本节点，与 Vue 响应式重渲染偶有冲突；
//       若翻译态下交互出现异常，可对易变区域加 class="notranslate" 规避。

const QUERY_KEY = 'lang'
const INJECT_FLAG = '__crirpGoogTranslate'
const COOKIE_NAME = 'googtrans'

// 中文标签：zh / zh-cn / zh-tw / zh-hk / zh-hans / zh-hant / zh_...
function isChineseLang(lang) {
  const l = String(lang || '').toLowerCase().trim()
  return l === 'zh' || l.startsWith('zh-') || l.startsWith('zh_')
}

// 浏览器首选语言：navigator.languages 优先，回退 navigator.language / userLanguage
function getBrowserLanguage() {
  if (navigator.languages && navigator.languages.length) return navigator.languages[0]
  return navigator.language || navigator.userLanguage || ''
}

// 浏览器语言码 → Google 翻译目标语言码。
// 多数情况取主标签（「-」/「_」前的小写两字母）即可命中 Google 码；
// 个别浏览器码与 Google 码不同，在此显式映射。
const SPECIAL_LANG = {
  fil: 'tl',  // Filipino → Tagalog
  tl: 'tl',
  he: 'iw',   // 希伯来文（Google 用旧码 iw）
  jv: 'jw',   // 爪哇文
  nb: 'no',   // 挪威语（书面）→ no
  nn: 'no',
}
function mapToGoogleLang(lang) {
  const l = String(lang || '').toLowerCase().trim()
  if (SPECIAL_LANG[l]) return SPECIAL_LANG[l]
  const primary = l.split(/[-_]/)[0]
  if (SPECIAL_LANG[primary]) return SPECIAL_LANG[primary]
  return primary || 'en'
}

// 读取已有 googtrans cookie 的目标语言（形如 /auto/en → en）
function readCookieTarget() {
  const m = document.cookie.match(new RegExp('(?:^|;\\s*)' + COOKIE_NAME + '=/[^/]*/([^;/]+)'))
  return m ? decodeURIComponent(m[1]) : ''
}

function setCookie(name, value, days) {
  const expires = days ? `;expires=${new Date(Date.now() + days * 864e5).toUTCString()}` : ''
  document.cookie = `${name}=${value};path=/${expires};SameSite=Lax`
}

export function resolveTargetLang() {
  const sp = new URLSearchParams(window.location.search || '')
  const q = (sp.get(QUERY_KEY) || '').trim().toLowerCase()
  if (q) {
    // 显式关闭
    if (['zh', 'cn', 'off', 'none', '0', 'false'].indexOf(q) !== -1) return ''
    return mapToGoogleLang(q)
  }
  const existing = readCookieTarget()
  if (existing) return existing
  const bl = getBrowserLanguage()
  return isChineseLang(bl) ? '' : mapToGoogleLang(bl)
}

// Google 翻译候选语言列表（与原脚本一致）
const INCLUDED_LANGS =
  'en,hr,cs,da,nl,fr,de,el,iw,hu,ga,it,ja,ko,pt,ro,ru,sr,es,th,vi,hmn,eo,my,mn,tl,ar,ms,no,la,lo'

// 注入 Google 翻译脚本与选择器容器（幂等）。
function injectGoogleTranslate(target) {
  if (window[INJECT_FLAG]) return
  window[INJECT_FLAG] = true

  // googtrans 必须在脚本加载前写入，Google 据此决定目标语言
  setCookie(COOKIE_NAME, `/auto/${target}`, 365)

  const head = document.querySelector('head')
  const setup = () => {
    if (!head || !document.body ||
        !(document.readyState === 'complete' || document.readyState === 'interactive')) {
      return setTimeout(setup, 300)
    }
    // 主脚本
    const s1 = document.createElement('script')
    s1.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit'
    head.appendChild(s1)

    // 翻译选择器容器（固定右下角，与原脚本位置一致）
    const el = document.createElement('div')
    el.id = 'google_translate_element'
    el.style = 'font-size:16px;position:fixed;bottom:10px;right:10px;cursor:pointer;z-index:99999;'
    document.body.appendChild(el)

    // 初始化回调（pageLanguage 用 zh-CN：本站即中文，比 auto 更稳）
    const s2 = document.createElement('script')
    s2.innerHTML =
      'function googleTranslateElementInit(){' +
      'new google.translate.TranslateElement({' +
      'layout: google.translate.TranslateElement.InlineLayout.SIMPLE,' +
      'multilanguagePage: true,' +
      "pageLanguage: 'zh-CN'," +
      `includedLanguages: '${INCLUDED_LANGS}',` +
      'autoDisplay: false' +
      "}, 'google_translate_element');}"
    head.appendChild(s2)
  }
  setup()
}

// 入口：判定是否需要翻译，需要则在 DOM 就绪后注入。
// 返回目标语言码（翻译时）或 ''（不翻译）。
export function initTranslate() {
  try {
    const browserLang = getBrowserLanguage()
    const allLangs = navigator.languages || []
    const target = resolveTargetLang()
    // 调试：打印浏览器语言（首选 + 全部偏好列表）与最终解析出的目标语言
    console.log('[translate] browser.language =', browserLang,
      '| navigator.languages =', JSON.stringify(allLangs),
      '| target =', target || '(不翻译)')
    if (!target) return ''
    injectGoogleTranslate(target)
    return target
  } catch (e) {
    return ''
  }
}
